import { useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardActions,
  CircularProgress,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useNotify } from 'react-admin';
import { useNavigate } from 'react-router-dom';
import Logo from './Logo';
import { apiUrl } from '../constants';

const passwordRules = [
  { label: 'At least 8 characters', test: (v: string) => v.length >= 8 },
  { label: 'At least 1 lowercase letter', test: (v: string) => /[a-z]/.test(v) },
  { label: 'At least 1 uppercase letter', test: (v: string) => /[A-Z]/.test(v) },
  { label: 'At least 1 special character', test: (v: string) => /[^A-Za-z0-9]/.test(v) },
];

const ForgotPassword = () => {
  const [step, setStep] = useState<1 | 2>(1);
  const [loading, setLoading] = useState(false);
  const [mobile, setMobile] = useState('');
  const [otp, setOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const notify = useNotify();
  const navigate = useNavigate();

  const handleRequestOtp = async () => {
    if (!mobile.trim()) {
      notify('Please enter your mobile number', { type: 'warning' });
      return;
    }
    setLoading(true);
    try {
      const response = await fetch(`${apiUrl}/auth/forgot-password/request-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mobile: mobile.trim() }),
      });
      if (!response.ok) {
        const error = await response.json().catch(() => null);
        throw new Error(error?.message || 'Failed to send OTP');
      }
      notify('OTP sent successfully', { type: 'success' });
      setStep(2);
    } catch (error: any) {
      notify(error.message || 'Failed to send OTP', { type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const allPasswordRulesPass = passwordRules.every((rule) => rule.test(newPassword));
  const passwordsMatch = newPassword === confirmPassword;

  const handleResetPassword = async () => {
    if (!otp.trim()) {
      notify('Please enter the OTP', { type: 'warning' });
      return;
    }
    if (!allPasswordRulesPass) {
      notify('Password does not meet requirements', { type: 'warning' });
      return;
    }
    if (!passwordsMatch) {
      notify('Passwords do not match', { type: 'warning' });
      return;
    }
    setLoading(true);
    try {
      const response = await fetch(`${apiUrl}/auth/forgot-password/verify-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mobile: mobile.trim(), otp: otp.trim(), newPassword }),
      });
      if (!response.ok) {
        const error = await response.json().catch(() => null);
        throw new Error(error?.message || 'Failed to reset password');
      }
      notify('Password reset successfully', { type: 'success' });
      navigate('/login');
    } catch (error: any) {
      notify(error.message || 'Failed to reset password', { type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100vh',
        alignItems: 'center',
        justifyContent: 'flex-start',
        background:
          'url(https://www.paymadi.com/_next/static/media/hero-1.896277d2.jpg)',
        backgroundRepeat: 'no-repeat',
        backgroundSize: 'cover',
      }}
    >
      <Card sx={{ minWidth: 300, marginTop: '6em' }}>
        <Box
          sx={{
            marginTop: '1.5em',
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          <Logo height={30} />
        </Box>
        <Box
          sx={{
            marginTop: '1em',
            display: 'flex',
            justifyContent: 'center',
            color: (theme) => theme.palette.grey[800],
          }}
        >
          Forgot Password
        </Box>

        {step === 1 ? (
          <Box sx={{ padding: '0 1em 1em 1em' }}>
            <Box sx={{ marginTop: '1em' }}>
              <TextField
                autoFocus
                label="Mobile Number"
                value={mobile}
                onChange={(e) => setMobile(e.target.value)}
                disabled={loading}
                fullWidth
                size="small"
              />
            </Box>
            <CardActions sx={{ padding: '1em 0 0 0' }}>
              <Button
                variant="contained"
                color="primary"
                disabled={loading}
                fullWidth
                onClick={handleRequestOtp}
              >
                {loading && <CircularProgress size={25} thickness={2} sx={{ mr: 1 }} />}
                Send OTP
              </Button>
            </CardActions>
            <Box sx={{ textAlign: 'center', mt: 1 }}>
              <Button size="small" onClick={() => navigate('/login')}>
                Back to Login
              </Button>
            </Box>
          </Box>
        ) : (
          <Box sx={{ padding: '0 1em 1em 1em' }}>
            <Box sx={{ marginTop: '1em' }}>
              <TextField
                autoFocus
                label="OTP"
                value={otp}
                onChange={(e) => setOtp(e.target.value)}
                disabled={loading}
                fullWidth
                size="small"
              />
            </Box>
            <Box sx={{ marginTop: '1em' }}>
              <TextField
                label="New Password"
                type={showPassword ? 'text' : 'password'}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                disabled={loading}
                fullWidth
                size="small"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                        size="small"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
            {newPassword && (
              <Box sx={{ mt: 0.5 }}>
                {passwordRules.map((rule) => (
                  <Typography
                    key={rule.label}
                    variant="caption"
                    sx={{
                      display: 'block',
                      color: rule.test(newPassword) ? 'success.main' : 'error.main',
                    }}
                  >
                    {rule.test(newPassword) ? '✓' : '✗'} {rule.label}
                  </Typography>
                ))}
              </Box>
            )}
            <Box sx={{ marginTop: '1em' }}>
              <TextField
                label="Confirm Password"
                type={showConfirmPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                disabled={loading}
                fullWidth
                size="small"
                error={!!confirmPassword && !passwordsMatch}
                helperText={
                  confirmPassword && !passwordsMatch
                    ? 'Passwords do not match'
                    : ''
                }
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        edge="end"
                        size="small"
                      >
                        {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
            <CardActions sx={{ padding: '1em 0 0 0' }}>
              <Button
                variant="contained"
                color="primary"
                disabled={loading}
                fullWidth
                onClick={handleResetPassword}
              >
                {loading && <CircularProgress size={25} thickness={2} sx={{ mr: 1 }} />}
                Reset Password
              </Button>
            </CardActions>
            <Box sx={{ textAlign: 'center', mt: 1 }}>
              <Button size="small" onClick={() => navigate('/login')}>
                Back to Login
              </Button>
            </Box>
          </Box>
        )}
      </Card>
    </Box>
  );
};

export default ForgotPassword;
